/* Film scene layout: the artifact built these diagrams as SVG strings with
 * hand-rolled text wrapping (SVG has no line boxes). The wrapping stays, but it
 * now returns positioned lines for React to render as <tspan>s, and wire
 * routing returns a path — no markup is concatenated anywhere.
 */
import type { FilmBoxSpec, FilmFrameSpec, FilmItemSpec, FilmScene } from '../types';

export type Placed = FilmBoxSpec | FilmFrameSpec;

export function itemsById(scene: FilmScene): Record<string, FilmItemSpec> {
  const byId: Record<string, FilmItemSpec> = {};
  for (const item of scene.items) byId[item.id] = item;
  return byId;
}

export function isPlaced(item: FilmItemSpec | undefined): item is Placed {
  return !!item && (item.k === 'box' || item.k === 'chip' || item.k === 'frame');
}

/* ~0.545em per character at this size, which is what the artifact measured. */
export function wrapText(str: string, w: number, size: number): string[] {
  const max = Math.max(6, Math.floor(w / (size * 0.545)));
  const lines: string[] = [];
  for (const para of String(str).split('\n')) {
    let line = '';
    for (const word of para.split(' ')) {
      const test = line ? line + ' ' + word : word;
      if (test.length > max && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    lines.push(line);
  }
  return lines;
}

export interface TextLine {
  x: number;
  y: number;
  text: string;
}

export function textLines(lines: string[], cx: number, cy: number, size: number): TextLine[] {
  const lh = size * 1.28;
  const y0 = cy - ((lines.length - 1) * lh) / 2 + size * 0.34;
  return lines.map((text, i) => ({ x: cx, y: y0 + i * lh, text }));
}

/* Wire routing: across, down, or up — the three cases the artifact handled. */
export function wirePath(a: Placed, b: Placed): string {
  if (b.x > a.x + a.w - 10) {
    const x1 = a.x + a.w;
    const y1 = a.y + a.h / 2;
    const x2 = b.x;
    const y2 = b.y + b.h / 2;
    const dx = Math.max(22, (x2 - x1) * 0.5);
    return `M${x1} ${y1} C${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  }
  if (b.y > a.y + a.h - 6) {
    const x1 = a.x + a.w / 2;
    const y1 = a.y + a.h;
    const x2 = b.x + b.w / 2;
    const y2 = b.y;
    const dy = Math.max(18, (y2 - y1) * 0.5);
    return `M${x1} ${y1} C${x1} ${y1 + dy}, ${x2} ${y2 - dy}, ${x2} ${y2}`;
  }
  const x1 = a.x + a.w / 2;
  const y1 = a.y;
  const x2 = b.x + b.w / 2;
  const y2 = b.y + b.h;
  const dy2 = Math.max(18, (y1 - y2) * 0.5);
  return `M${x1} ${y1} C${x1} ${y1 - dy2}, ${x2} ${y2 + dy2}, ${x2} ${y2}`;
}

export function sparkGeometry(item: {
  x: number;
  y: number;
  w: number;
  h: number;
  pts: number[];
}): { points: string; area: string } {
  const n = item.pts.length;
  let points = '';
  let area = '';
  for (let k = 0; k < n; k++) {
    const px = item.x + (item.w * k) / (n - 1);
    const py = item.y + item.h * (1 - item.pts[k]);
    points += (k ? ' ' : '') + px + ',' + py;
    area += (k ? ' L' : 'M') + px + ' ' + py;
  }
  area += ` L${item.x + item.w} ${item.y + item.h} L${item.x} ${item.y + item.h} Z`;
  return { points, area };
}

/* Which of a step's three roles each item plays, for the class list. */
export interface StepRoles {
  on: Set<string>;
  hot: Set<string>;
  dim: Set<string>;
}

export function stepRoles(step: { on: string[]; hot?: string[]; dim?: string[] }): StepRoles {
  return { on: new Set(step.on), hot: new Set(step.hot ?? []), dim: new Set(step.dim ?? []) };
}

export function filmClass(id: string, roles: StepRoles): string {
  return (
    'fi' +
    (roles.on.has(id) ? ' on' : '') +
    (roles.hot.has(id) ? ' hot' : '') +
    (roles.dim.has(id) ? ' dim' : '')
  );
}
